
import React, { useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Heart } from 'lucide-react';
import { useVideoInteraction } from '@/hooks/use-video-interaction';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

export interface VideoControlsProps {
  videoId: string;
  onTip: (amount: number) => void;
  tipAmount: number;
  setTipAmount: (amount: number) => void;
}

const VideoControls: React.FC<VideoControlsProps> = ({
  videoId,
  onTip,
  tipAmount,
  setTipAmount
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { isLiked, isPending, toggleLike } = useVideoInteraction(videoId);
  
  // Create a dedicated flag for preventing like operations
  const isLikeInProgressRef = useRef(false);

  // Update refs when isLiked changes
  useEffect(() => {
    isLikeInProgressRef.current = false;
  }, [isLiked]);

  const handleLikeClick = () => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to like videos",
        variant: "destructive"
      });
      return;
    }

    // Comprehensive check to prevent multiple likes
    if (isLiked || isPending || isLikeInProgressRef.current) {
      console.log('Video like operation prevented:', {
        reason: isLiked ? 'Already liked' : isPending ? 'Like in progress' : 'Like operation locked',
        videoId,
        isLiked
      });
      return;
    }
    
    // Set flag immediately to prevent duplicate calls
    isLikeInProgressRef.current = true;
    
    // Call the actual like function
    toggleLike();
  };

  return (
    <div className="absolute bottom-4 right-4 flex flex-col gap-2">
      <Button
        variant="ghost"
        size="icon"
        className={`bg-black/50 hover:bg-black/70 text-white rounded-full ${
          isLiked ? 'text-red-500' : ''
        }`}
        onClick={handleLikeClick}
        disabled={isLiked || isPending || isLikeInProgressRef.current}
        data-video-id={videoId}
        data-liked={isLiked ? 'true' : 'false'}
      >
        <Heart className={`h-6 w-6 ${isLiked ? 'fill-current' : ''}`} />
      </Button>
      <Button
        variant="ghost"
        className="bg-black/50 hover:bg-black/70 text-white rounded-full"
        onClick={() => onTip(tipAmount)}
      >
        Tip {tipAmount}
      </Button>
    </div>
  );
};

export default VideoControls;
